import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import {
  acceptGoogleInvite,
  googleBrowserLogin,
  logoutBrowserSession,
  refreshBrowserSession,
} from '@/auth/auth-session';
import type { SessionUser } from '@/types';

type AuthStatus = 'loading' | 'authenticated' | 'anonymous';
interface AuthContextValue {
  status: AuthStatus;
  user: SessionUser | null;
  loginWithGoogle: (idToken: string) => Promise<void>;
  acceptInvite: (idToken: string, token: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<SessionUser | null>(null);

  useEffect(() => {
    let active = true;
    refreshBrowserSession().then((session) => {
      if (!active) return;
      setUser(session?.user ?? null);
      setStatus(session ? 'authenticated' : 'anonymous');
    });
    const expired = () => { setUser(null); setStatus('anonymous'); };
    window.addEventListener('futsaly-auth-expired', expired);
    return () => { active = false; window.removeEventListener('futsaly-auth-expired', expired); };
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    status,
    user,
    loginWithGoogle: async (idToken) => {
      const session = await googleBrowserLogin(idToken);
      setUser(session.user);
      setStatus('authenticated');
    },
    acceptInvite: async (idToken, token) => {
      const session = await acceptGoogleInvite(idToken, token);
      setUser(session.user);
      setStatus('authenticated');
    },
    logout: async () => {
      await logoutBrowserSession();
      setUser(null);
      setStatus('anonymous');
    },
  }), [status, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('AuthProvider is missing');
  return value;
}
